import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

// ✅ Import correct function
import { getPackagesByLabId } from "../../services/LabTestApi/packageService";
import GetPackageDetails from "./GetPackageDetails";
import ReplaceCartPopup from "../LabTest/ReplaceCartPopup";
import { usePackageCartActions } from "../../hooks/usePackageCartActions";
import { useCartActions } from "../../hooks/useCartActions";
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from "../../Redux/wishlistSlice";
import { fetchAllRatings } from "../../Redux/ratingSlice";
import LoginPopup from "../common/LoginPopup";
import Coupon from "../Coupon/Coupon";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../../index.css";

import { 
  FaShoppingCart, FaBox, FaStar, FaHeart, FaRegHeart, FaArrowRight,
  FaChevronLeft, FaChevronRight, FaClock, FaFlask, FaTint, 
  FaHeartbeat, FaLungs, FaShieldAlt, FaUserMd, FaLeaf, FaBrain,
  FaBolt, FaGem, FaFire, FaBug, FaCrown, FaEye
} from "react-icons/fa";

export default function PackagesSection({ labId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  console.log('🔍 ===== PACKAGES SECTION MOUNTED =====');
  console.log('🔍 labId received:', labId);

  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const cart = useSelector((state) => state.cart?.items || []);
  const ratings = useSelector((state) => state.rating?.ratings || {});
  const ratingLoading = useSelector((state) => state.rating?.loading || false);
  const wishlistItems = useSelector((state) => state.wishlist?.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist?.loading || false);
  
  const auth = useSelector((state) => state.auth) || {};
  const user = auth?.user || null;

  console.log('🔍 Auth state:', auth);
  console.log('🔍 User from Redux:', user);

  const { 
    addWithReplaceCheck, 
    removeItem, 
    LoginPopupComponent
  } = useCartActions();

  const { addPackage, removePackage } = usePackageCartActions();

  // ✅ Embla Carousel with Autoplay - Manual scroll only
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      slidesToScroll: 1,
      breakpoints: {
        '(min-width: 576px)': { slidesToScroll: 1 },
        '(min-width: 768px)': { slidesToScroll: 1 },
        '(min-width: 992px)': { slidesToScroll: 1 },
        '(min-width: 1200px)': { slidesToScroll: 1 },
      }
    },
    [Autoplay({ 
      delay: 3000, 
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    })]
  );

  // ✅ Get User ID from Redux or localStorage
  const getUserId = () => {
    console.log('🔍 Getting User ID...');
    if (user?.userId) {
      console.log('✅ User ID from Redux:', user.userId);
      return user.userId;
    }
    try {
      const userData = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
      console.log('🔍 User data from localStorage:', userData);
      const userId = userData?.userId || null;
      console.log('✅ User ID from localStorage:', userId);
      return userId;
    } catch (error) {
      console.error('❌ Error parsing user from localStorage:', error);
      return null;
    }
  };

  // ✅ Check if user is logged in
  const isUserLoggedIn = () => {
    const loggedIn = !!getUserId();
    console.log('🔍 Is user logged in?', loggedIn);
    return loggedIn;
  };

  // ✅ Load Wishlist using Redux with error handling
  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) {
      console.log('📌 No user ID found, skipping wishlist load');
      return;
    }
    
    try {
      console.log('📌 Loading wishlist for user:', userId);
      await dispatch(fetchWishlist(userId)).unwrap();
      console.log('✅ Wishlist loaded successfully');
    } catch (error) {
      if (error?.status === 404) {
        console.log('⚠️ Wishlist API not available (404) - continuing without wishlist');
        return;
      }
      console.error('❌ Error loading wishlist:', error);
    }
  };

  // ✅ Check if in wishlist with safe check
  const isInWishlist = (productId, categoryId = 3) => {
    if (!wishlistItems || !Array.isArray(wishlistItems)) return false;
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  // ✅ Toggle Wishlist with Optimistic Update
  const toggleWishlist = async (pkg, e) => {
    e.stopPropagation();
    
    console.log('❤️ Toggle wishlist clicked for package:', pkg.packageId);
    console.log('🔍 isUserLoggedIn():', isUserLoggedIn());
    
    if (!isUserLoggedIn()) {
      console.log('🚨 User not logged in - opening login popup for wishlist');
      setShowLoginPopup(true);
      return;
    }

    console.log('✅ User is logged in, proceeding with wishlist toggle');

    const userId = getUserId();
    const productId = pkg.packageId;
    const categoryId = 3;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      if (error?.status !== 404) {
        alert('Failed to update wishlist. Please try again.');
      }
    }
  };

  // ✅ Handle Add to Cart
  const handleAddToCart = (pkg, e) => {
    e.stopPropagation();
    
    console.log('🛒 Add to cart clicked for package:', pkg.packageId);
    console.log('🔍 isUserLoggedIn():', isUserLoggedIn());
    
    if (!isUserLoggedIn()) {
      console.log('🚨 User not logged in - opening login popup for cart');
      setShowLoginPopup(true);
      return;
    }
    
    const payload = {
      productId: pkg.packageId,
      categoryId: 3,
      price: Number(pkg.packagePrice) || 0,
      storeId: Number(pkg.labId) || 0,
      productName: pkg.packageName || "",
    };
    
    const added = isPackageAdded(pkg.packageId);
    if (added) {
      removeItem(payload);
    } else {
      addWithReplaceCheck(payload);
    }
  };

  const getPackageRating = (packageId) => {
    const key = `${packageId}_3`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  const renderStars = (rating, size = 14) => {
    const stars = [];
    const roundedRating = Math.round(rating || 0);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar
          key={i}
          size={size}
          className={`${
            i <= roundedRating
              ? 'text-yellow-500 fill-yellow-500'
              : 'text-gray-300'
          }`}
        />
      );
    }
    return stars;
  };

  // ✅ Get tests count from details array
  const getTestsCount = (pkg) => {
    if (pkg.details && Array.isArray(pkg.details)) {
      return pkg.details.length;
    }
    if (pkg.testPackageDetailsJson) {
      try {
        const tests = JSON.parse(pkg.testPackageDetailsJson);
        return Array.isArray(tests) ? tests.length : 0;
      } catch {
        return 0;
      }
    }
    return 0;
  };

  // ✅ Get package icon based on name
  const getPackageIcon = (name) => {
    if (!name) return <FaBox size={24} className="text-red-500" />;
    const nameLower = name.toLowerCase();
    if (nameLower.includes('diabetes') || nameLower.includes('sugar')) 
      return <FaTint size={24} className="text-blue-500" />;
    if (nameLower.includes('heart') || nameLower.includes('cardiac')) 
      return <FaHeartbeat size={24} className="text-red-500" />;
    if (nameLower.includes('thyroid')) 
      return <FaLungs size={24} className="text-purple-500" />;
    if (nameLower.includes('liver') || nameLower.includes('lft')) 
      return <FaLeaf size={24} className="text-green-500" />;
    if (nameLower.includes('full') || nameLower.includes('body')) 
      return <FaShieldAlt size={24} className="text-indigo-500" />;
    if (nameLower.includes('kidney') || nameLower.includes('renal')) 
      return <FaTint size={24} className="text-cyan-500" />;
    if (nameLower.includes('brain') || nameLower.includes('neuro')) 
      return <FaBrain size={24} className="text-pink-500" />;
    if (nameLower.includes('bone') || nameLower.includes('joint')) 
      return <FaBug size={24} className="text-amber-500" />;
    if (nameLower.includes('eye') || nameLower.includes('vision')) 
      return <FaEye size={24} className="text-teal-500" />;
    if (nameLower.includes('covid') || nameLower.includes('corona')) 
      return <FaBug size={24} className="text-red-600" />;
    if (nameLower.includes('fever') || nameLower.includes('infection')) 
      return <FaFire size={24} className="text-orange-500" />;
    if (nameLower.includes('premium') || nameLower.includes('gold')) 
      return <FaCrown size={24} className="text-yellow-500" />;
    return <FaBox size={24} className="text-red-500" />;
  };

  // ================= LOAD PACKAGES =================
  useEffect(() => {
    const loadPackages = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('========================================');
        console.log('📌 ===== PACKAGES SECTION DEBUG =====');
        console.log('📌 labId received:', labId);
        console.log('📌 labId type:', typeof labId);
        console.log('📌 labId as Number:', Number(labId));

        const filtered = await getPackagesByLabId(labId);
        
        console.log('📌 Filtered packages count:', filtered.length);
        console.log('📌 Filtered packages:', filtered);
        
        filtered.forEach((pkg, index) => {
          console.log(`📌 Package ${index + 1}:`, {
            id: pkg.packageId,
            name: pkg.packageName,
            labId: pkg.labId,
            isActive: pkg.isActive,
            price: pkg.packagePrice,
            totalPrice: pkg.totalPrice,
            discountPrice: pkg.discountPrice,
            tests: pkg.details?.length || 0
          });
        });
        
        console.log('========================================');

        setPackages(filtered);

        if (filtered.length > 0) {
          const ratingProducts = filtered.map(pkg => ({
            productId: pkg.packageId,
            categoryId: 3
          }));
          try {
            await dispatch(fetchAllRatings(ratingProducts));
          } catch (error) {
            console.error('❌ Error loading ratings:', error);
          }
        }

        await loadWishlist();

      } catch (error) {
        console.error('❌ Package API Error:', error);
        setError(error.message || 'Failed to load packages');
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };

    if (labId) {
      loadPackages();
    } else {
      console.warn('⚠️ No labId provided, skipping package load');
      setLoading(false);
    }
  }, [labId, dispatch]);

  // ================= HANDLERS =================
  const handleCardClick = (pkg) => {
    setSelectedPackageId(pkg.packageId);
    setShowPackageDetails(true);
  };

  const isPackageAdded = (packageId) =>
    cart?.some(
      (item) =>
        Number(item.productId) === Number(packageId) &&
        Number(item.categoryId) === 3
    );

  const handleViewAll = () => {
    navigate('/all-packages');
  };

  // ✅ Manual scroll functions (no dots)
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // ================= ERROR STATE =================
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-4">
        <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mb-4">
          <FaBox size={32} className="text-red-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-700 mb-2">
          Error Loading Packages
        </h3>
        <p className="text-sm text-gray-500 max-w-sm text-center">
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-6 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // ================= EMPTY STATE =================
  if (!loading && packages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-4">
        <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mb-4">
          <FaBox size={32} className="text-red-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-700 mb-2">
          No Packages Available
        </h3>
        <p className="text-sm text-gray-500 max-w-sm text-center">
          This lab currently has no packages listed.
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Lab ID: {labId}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Try checking if labId is correct or if packages exist for this lab.
        </p>
      </div>
    );
  }

  // ================= LOADING =================
  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-red-500 border-t-transparent"></div>
          <p className="mt-3 text-gray-500 text-sm">Loading packages...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
      
      {/* Header Section */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-3">
          <FaFlask size={12} className="text-yellow-400" />
          <span className="text-xs font-bold tracking-wide">LAB PACKAGES</span>
          <FaStar size={12} className="text-yellow-400" />
        </div>
        
        <h4 className="text-2xl md:text-3xl font-extrabold">
          <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
            Available
          </span>{" "}
          <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
            Packages
          </span>
        </h4>
        
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          Complete health checkup packages from this lab at best prices
        </p>

        <div className="mt-2 text-xs text-gray-400">
          Lab ID: {labId} • {packages.length} packages found
        </div>

        {/* View All Button */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleViewAll}
            className="group flex items-center gap-2 px-6 py-2 rounded-full border-2 border-red-500 text-red-500 font-semibold text-sm hover:bg-red-50 transition-all duration-300"
          >
            <span>View All Packages</span>
            <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" size={14} />
          </button>
        </div>
      </div>

      {/* ❌ DOTS REMOVED - No dots indicator */}

      {/* Embla Carousel - Manual Scroll Only */}
      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {packages.map((pkg) => {
              const added = isPackageAdded(pkg.packageId);
              const inWishlist = isInWishlist(pkg.packageId, 3);
              const testsCount = getTestsCount(pkg);
              
              const finalPrice = Number(pkg.packagePrice) || 0;
              const originalPrice = Number(pkg.totalPrice) || 0;
              const discountAmount = Number(pkg.discountPrice) || 0;
              
              const hasDiscount = discountAmount > 0 && originalPrice > 0 && finalPrice < originalPrice;
              
              const discountPercent = originalPrice > 0 && discountAmount > 0 
                ? Math.round((discountAmount / originalPrice) * 100) 
                : 0;
              
              const { average: averageRating, total: totalReviews } = getPackageRating(pkg.packageId);
              const labIdForCoupon = pkg.labId || labId || 0;

              return (
                <div key={pkg.packageId} className="flex-[0_0_100%] sm:flex-[0_0_50%] md:flex-[0_0_33.333%] lg:flex-[0_0_25%] xl:flex-[0_0_20%] min-w-0 px-2">
                  <div
                    className="group relative bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 cursor-pointer overflow-hidden"
                    onClick={() => handleCardClick(pkg)}
                  >
                    {/* Popular Badge - Top Right */}
                    {pkg.isPopular && (
                      <div className="absolute top-2 right-2 z-10">
                        <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                          <FaStar size={9} className="fill-white" />
                          POPULAR
                        </div>
                      </div>
                    )}

                    {/* Discount Badge - Top Left */}
                    {hasDiscount && discountAmount > 0 && (
                      <div className="absolute top-2 left-2 z-10">
                        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md">
                          {discountPercent}% OFF
                        </div>
                      </div>
                    )}

                    {/* Fasting Badge - Below Discount */}
                    {pkg.isFasting && (
                      <div className="absolute top-8 left-2 z-10">
                        <div className="bg-blue-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                          <FaClock size={8} />
                          Fasting
                        </div>
                      </div>
                    )}

                    {/* Wishlist Button */}
                    <button
                      onClick={(e) => toggleWishlist(pkg, e)}
                      className={`absolute z-10 p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200
                        ${pkg.isPopular ? 'top-9 right-2' : 'top-2 right-2'}`}
                      disabled={wishlistLoading}
                    >
                      {wishlistLoading ? (
                        <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        inWishlist ? (
                          <FaHeart size={14} className="text-red-500 fill-red-500" />
                        ) : (
                          <FaRegHeart size={14} className="text-gray-400 hover:text-red-500" />
                        )
                      )}
                    </button>

                    {/* Card Content */}
                    <div className="p-4 flex flex-col items-center">
                      {/* Package Icon */}
                      <div className="w-14 h-14 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                        {getPackageIcon(pkg.packageName)}
                      </div>

                      {/* Package Name */}
                      <h6 className="text-sm font-semibold text-gray-800 text-center line-clamp-2 min-h-[40px] group-hover:text-red-600 transition-colors">
                        {pkg.packageName || 'Unnamed Package'}
                      </h6>

                      {/* Rating Stars */}
                      <div className="flex justify-center items-center gap-1 mt-1">
                        <div className="flex">
                          {renderStars(averageRating, 12)}
                        </div>
                        {ratingLoading ? (
                          <span className="text-[9px] text-gray-400">...</span>
                        ) : (
                          <span className="text-[9px] text-gray-500">
                            ({totalReviews})
                          </span>
                        )}
                      </div>

                      {/* Lab Name & Tests Count */}
                      <div className="flex items-center justify-center gap-1.5 mt-1">
                        {pkg.labName && (
                          <span className="text-[10px] text-gray-400 truncate max-w-[80px]">
                            {pkg.labName}
                          </span>
                        )}
                        <span className="text-[9px] text-gray-400">•</span>
                        <span className="text-[9px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          {testsCount} Tests
                        </span>
                      </div>

                      {/* ✅ PRICE AND ADD BUTTON WITH COUPON SUPPORT */}
                      <div className="w-full mt-3 pt-2 border-t border-gray-100">
                        {hasDiscount ? (
                          // ---------- PRICE SECTION (with discount) ----------
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="text-sm font-bold text-green-600">
                                ₹{finalPrice.toLocaleString()}
                              </div>
                              {originalPrice > 0 && (
                                <div className="text-[9px] line-through text-gray-400">
                                  ₹{originalPrice.toLocaleString()}
                                </div>
                              )}
                              {discountAmount > 0 && (
                                <div className="text-[8px] text-red-500 font-semibold">
                                  Save ₹{discountAmount.toLocaleString()} ({discountPercent}% OFF)
                                </div>
                              )}
                            </div>

                            {/* ✅ BIGGER Add to Cart Button */}
                            <button
                              onClick={(e) => handleAddToCart(pkg, e)}
                              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[11px] font-semibold border transition-all duration-200
                              ${
                                added
                                  ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                                  : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"
                              }`}
                            >
                              <FaShoppingCart size={11} />
                              <span>{added ? "Remove" : "Add"}</span>
                            </button>
                          </div>
                        ) : (
                          // ---------- COUPON SECTION (no discount) ----------
                          <div className="w-full">
                            <div className="flex justify-between items-center">
                              <div>
                                {finalPrice > 0 && (
                                  <div className="text-sm font-bold text-green-600">
                                    ₹{finalPrice.toLocaleString()}
                                  </div>
                                )}
                                {originalPrice > 0 && originalPrice !== finalPrice && (
                                  <div className="text-[9px] line-through text-gray-400">
                                    ₹{originalPrice.toLocaleString()}
                                  </div>
                                )}
                                {originalPrice === finalPrice && originalPrice > 0 && (
                                  <div className="text-[9px] text-gray-400">No Discount</div>
                                )}
                                {originalPrice === 0 && finalPrice === 0 && (
                                  <div className="text-[9px] text-gray-400">Price not available</div>
                                )}
                              </div>

                              {/* ✅ BIGGER Add to Cart Button */}
                              <button
                                onClick={(e) => handleAddToCart(pkg, e)}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[11px] font-semibold border transition-all duration-200
                                ${
                                  added
                                    ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                                    : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"
                                }`}
                              >
                                <FaShoppingCart size={11} />
                                <span>{added ? "Remove" : "Add"}</span>
                              </button>
                            </div>

                            {/* ✅ Coupon Component - Below Price & Add Button */}
                            <div className="mt-1.5">
                              <Coupon
                                labId={labIdForCoupon}
                                userId={getUserId()}
                                testPrice={originalPrice}
                                sellingPrice={finalPrice}
                                discount={0}
                                showCopyButton={false}
                                showAddToCart={false}
                                isInCart={added}
                                onAddToCart={() => handleAddToCart(pkg, { stopPropagation: () => {} })}
                                onRemoveFromCart={() => handleAddToCart(pkg, { stopPropagation: () => {} })}
                                className="w-full"
                                loadingText="Loading coupon..."
                                noCouponText="No coupon available"
                                labelText="🎫 No Discount - Coupon"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bottom Border Animation */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent 
                    transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ✅ Navigation Buttons - Manual Scroll Only */}
        {packages.length > 5 && (
          <>
            <button
              onClick={scrollPrev}
              className="absolute -left-3 md:-left-4 top-1/2 -translate-y-1/2 z-20
              bg-white hover:bg-red-500 text-red-500 hover:text-white 
              shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
              focus:outline-none"
              aria-label="Previous slide"
            >
              <FaChevronLeft size={14} />
            </button>
            <button
              onClick={scrollNext}
              className="absolute -right-3 md:-right-4 top-1/2 -translate-y-1/2 z-20
              bg-white hover:bg-red-500 text-red-500 hover:text-white 
              shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
              focus:outline-none"
              aria-label="Next slide"
            >
              <FaChevronRight size={14} />
            </button>
          </>
        )}
      </div>

      {/* Trust Badges */}
      {packages.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-[9px] font-bold">✓</span>
              </div>
              <span>Affordable Packages</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-[9px] font-bold">⚡</span>
              </div>
              <span>NABL Certified Labs</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-[9px] font-bold">🏆</span>
              </div>
              <span>Free Home Pickup</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-[9px] font-bold">❤️</span>
              </div>
              <span>10L+ Happy Users</span>
            </div>
          </div>
        </div>
      )}

      {/* DETAILS MODAL */}
      {showPackageDetails && (
        <GetPackageDetails
          id={selectedPackageId}
          onClose={() => setShowPackageDetails(false)}
        />
      )}

      <ReplaceCartPopup />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
    
    {/* ✅ Login Popup - Local for wishlist and cart */}
    <LoginPopup
      isOpen={showLoginPopup}
      onClose={() => setShowLoginPopup(false)}
    />
    
    {/* ✅ LoginPopupComponent from useCartActions */}
    {LoginPopupComponent}
    </>
  );
}